import { Button, type ComboboxData, Select, Stack } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import saveAs from "file-saver";
import type { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import type { ApiResponse } from "../../../../api";
import api from "../../../../api";
import { DateRangeInput } from "../../../../mantine/dates/DateRangeInput/DateRangeInput";
import { DateRangePresets } from "../../../../mantine/dates/DateRangeInput/DateRangeInput.constants";
import { useToast } from "../../../../mantine/notifications/useToast";
import urls from "../../../../urls";
import type { TemplateMeter } from "../../../../utils/backend-types";
import {
  LUXON_END_OF_LAST_YEAR,
  LUXON_START_OF_LAST_YEAR
} from "../../../../utils/dates";
import type { Unit } from "../../../../utils/enums";
import { b64toBlob } from "../../../../utils/files/b64toBlob";
import { BasicConfirmationModal } from "../../../BuildingBlocks/Layout/Modals/BasicConfirmationModal/BasicConfirmationModal";
import { LoadOrError } from "../../../LoadOrError/LoadOrError";
import "./EnergyDataUploadExplanationWithTemplateDownload.scss";

const START_DATE = LUXON_START_OF_LAST_YEAR;
const END_DATE = LUXON_END_OF_LAST_YEAR;

interface EnergyDataTemplateDownloadResponse {
  encodedFileData: string;
  fileName: string;
  warnings: Array<string>;
}

interface DateRange {
  startDate: DateTime | null;
  endDate: DateTime | null;
}

interface EnergyDataUploadExplanationWithTemplateDownloadProps {
  variantId: number;
  defaultMeterId?: number;
}

function EnergyDataUploadExplanationWithTemplateDownload({
  variantId,
  defaultMeterId
}: EnergyDataUploadExplanationWithTemplateDownloadProps) {
  const { showErrorToast } = useToast();
  const [downloadResponse, setDownloadResponse] = useState<
    ApiResponse<EnergyDataTemplateDownloadResponse> | undefined
  >();
  const [downloadWarnings, setDownloadWarnings] = useState<Array<string>>([]);
  const [isDownloading, setIsDownLoading] = useState(false);

  function handleExcelFile(
    response: ApiResponse<EnergyDataTemplateDownloadResponse>
  ) {
    setDownloadResponse(response);

    if (response.data.warnings && response.data.warnings.length > 0) {
      setDownloadWarnings(response.data.warnings);
    } else {
      saveDownload(response);
    }
  }

  function handleClickDownloadTemplate(
    dates: DateRange,
    selectedMeter: number | null,
    unit?: Unit
  ) {
    if (dates.startDate === null || dates.endDate === null) {
      return;
    }

    if (selectedMeter && !unit) {
      return;
    }

    setIsDownLoading(true);
    api
      .get<EnergyDataTemplateDownloadResponse>(
        urls.api.energyDataTemplateDownload(
          variantId,
          dates.startDate.toISO() as string, // todo auto convert sent dates in axios hooks // noqa: MISSING_TODO_LINK
          dates.endDate.toISO() as string, // todo auto convert sent dates in axios hooks // noqa: MISSING_TODO_LINK
          selectedMeter,
          unit
        )
      )
      .then((response) => {
        handleExcelFile(response);
      })
      .catch((error) => {
        showErrorToast(error);
      })
      .finally(() => setIsDownLoading(false));
  }

  function handleDownloadConfirmation() {
    if (downloadResponse) {
      saveDownload(downloadResponse);
    }

    setDownloadWarnings([]);
  }

  function saveDownload(
    response: ApiResponse<EnergyDataTemplateDownloadResponse>
  ) {
    const contentTypeHeader = response.headers["content-type"];
    const contentType =
      typeof contentTypeHeader === "string" ? contentTypeHeader : "";
    const blob = b64toBlob(response.data.encodedFileData, contentType);

    saveAs(blob, response.data.fileName);
    setDownloadResponse(undefined);
  }

  return (
    <div className="EnergyDataTemplateDownload">
      <div className="dropzone-overlay-section explanation">
        Sie können sich hier eine Dateivorlage für den Upload erstellen lassen.
        Bitte wählen Sie den Zeitraum, der in der Vorlage enthalten sein soll.
        Wenn Sie das opti.node-Datenformat <strong>für alle Zähler</strong>{" "}
        verwenden, muss das Format der hochgeladenen Datei der Vorlage
        entsprechen. Bei Bedarf können Sie aber Zeilen, Spalten oder
        Tabellenblätter löschen.
      </div>
      <Stack align="center" className="dropzone-overlay-section" gap="sm">
        <Controls
          defaultSelectedMeterId={defaultMeterId}
          isDownloading={isDownloading}
          variantId={variantId}
          onClickDownload={handleClickDownloadTemplate}
        />
      </Stack>
      <BasicConfirmationModal
        cancelButtonText="Vorlage trotzdem herunterladen"
        confirmationButtonText="OK"
        opened={downloadWarnings.length > 0}
        title="Hinweis"
        toggleModal={() => setDownloadWarnings([])}
        onCancel={handleDownloadConfirmation}
        onConfirm={() => setDownloadWarnings([])}
      >
        <div className="energy-data-template-download-confirmation-body">
          {downloadWarnings.map((warning) => (
            <p key={warning}>{warning}</p>
          ))}
        </div>
      </BasicConfirmationModal>
    </div>
  );
}

interface ControlsProps {
  defaultSelectedMeterId?: number;
  isDownloading: boolean;
  variantId: number;
  onClickDownload: (
    dates: DateRange,
    selectedMeter: number | null,
    unit?: Unit
  ) => void;
}

function Controls({
  defaultSelectedMeterId,
  isDownloading,
  variantId,
  onClickDownload
}: ControlsProps) {
  const [dates, setDates] = useState<DateRange>({
    startDate: START_DATE,
    endDate: END_DATE
  });
  const {
    data: templateMeters,
    isLoading,
    error
  } = useQuery({
    queryKey: ["template-meters", { variantId }],
    queryFn: () => fetchTemplateMeters(variantId)
  });
  const [selectedMeter, setSelectedMeter] = useState<number | null>(
    defaultSelectedMeterId ?? null
  );
  const [selectedUnit, setSelectedUnit] = useState<Unit | undefined>();
  const meterData = useMemo<ComboboxData>(() => {
    const allMeters = { label: "Alle Zähler", value: "" };
    if (!templateMeters) {
      return [allMeters];
    }

    const groups = Array.from(
      new Set(templateMeters.map((meter) => meter.siteName))
    );

    const templateMetersData = groups.sort().map((group) => ({
      group: group,
      items: templateMeters
        .filter((meter) => meter.siteName === group)
        .map((meter) => ({
          label: meter.name,
          value: meter.id.toString()
        }))
        .sort((a, b) => (a.label < b.label ? -1 : a.label > b.label ? 1 : 0))
    }));

    return [allMeters, ...templateMetersData];
  }, [templateMeters]);

  useEffect(() => {
    const selectedTemplateMeter = templateMeters?.find(
      (meter) => meter.id === selectedMeter
    );

    if (selectedTemplateMeter) {
      setSelectedUnit(selectedTemplateMeter.defaultUnit);
    } else {
      setSelectedUnit(undefined);
    }
  }, [selectedMeter, templateMeters]);

  function handleMeterChange(newValue: string | null) {
    if (newValue === "" || newValue === null) {
      setSelectedMeter(null);
    } else {
      setSelectedMeter(parseInt(newValue, 10));
    }
  }

  function handleUnitChange(newValue: string | null) {
    if (!newValue) {
      return;
    }
    setSelectedUnit(newValue as Unit);
  }

  const canDownload = dates.startDate !== null && dates.endDate !== null;
  const selectedTemplateMeter = templateMeters?.find(
    (meter) => meter.id === selectedMeter
  );
  const unitOptions =
    selectedTemplateMeter?.units.map((unit) => ({
      label: unit[1],
      value: unit[0]
    })) ?? [];

  return (
    <LoadOrError error={error} loading={isLoading}>
      {templateMeters && (
        <>
          <DateRangeInput
            presets={{ type: DateRangePresets.YearsAndMonths }}
            value={[dates.startDate, dates.endDate]}
            onChange={(newRange) =>
              setDates({
                startDate: newRange[0] || null,
                endDate: newRange[1] || null
              })
            }
          />
          <Select
            data={meterData}
            defaultValue={selectedMeter?.toString() ?? ""}
            key={selectedMeter ?? "all"}
            name="template-meter-selection"
            placeholder="Zähler auswählen"
            required
            onChange={(newValue) => handleMeterChange(newValue)}
          />
          {selectedMeter && (
            <Select
              data={unitOptions}
              defaultValue={selectedUnit}
              placeholder="Einheit auswählen"
              required
              w={100}
              onChange={(newValue) => handleUnitChange(newValue)}
            />
          )}
          <Button
            disabled={!canDownload}
            loading={isDownloading}
            variant="default"
            onClick={() => onClickDownload(dates, selectedMeter, selectedUnit)}
          >
            Vorlage herunterladen
          </Button>
        </>
      )}
    </LoadOrError>
  );
}

async function fetchTemplateMeters(variantId: number) {
  const response = await api.get<TemplateMeter[]>(
    urls.api.energyDataTemplateMeters(variantId)
  );

  return response.data;
}

export { EnergyDataUploadExplanationWithTemplateDownload };
